import { useCallback, useEffect, useMemo, useRef } from 'react';
import { memo } from 'react';
import { LiquidityDepthChart, LiquidityDepthChartLoading } from '@/components/LiquidityDepthChart';
import { useAddLiquidityState } from '@/components/AddLiquidity/AddLiquidityProvider';
import { tryParsePrice } from '@/components/AddLiquidity/utils';
import { Bound } from '@/components/LiquidityDepthChart/types';

export { LiquidityDepthChartLoading as AddLiquidityDepthChartLoading };

/**
 * AddLiquidityDepthChart wraps LiquidityDepthChart with Add Liquidity context.
 * It connects the chart to the AddLiquidityProvider state and handles
 * price range changes from brush interactions.
 */
export const AddLiquidityDepthChart = memo(function AddLiquidityDepthChart() {
  const {
    positionId,
    baseToken: currencyA,
    quoteToken: currencyB,
    poolKey,
    poolState,
    tickAtLimit,
    priceInputs,
    setTickPricesState,
  } = useAddLiquidityState();

  const handleChangePriceLower = useCallback(
    (newValue: string) => {
      setTickPricesState((prev) => ({
        priceLower: newValue || '',
        priceUpper: prev.priceUpper,
      }));
    },
    [setTickPricesState],
  );

  const handleChangePriceUpper = useCallback(
    (newValue: string) => {
      setTickPricesState((prev) => ({
        priceLower: prev.priceLower,
        priceUpper: newValue || '',
      }));
    },
    [setTickPricesState],
  );

  const priceLower = tryParsePrice(currencyA, currencyB, priceInputs.lower.value);
  const priceUpper = tryParsePrice(currencyA, currencyB, priceInputs.upper.value);

  const currentPrice = Number(priceInputs.current.initial);

  const isSorted = currencyA && currencyB && currencyA.sortsBefore(currencyB);
  const invertPrices = !isSorted;

  // Calculate brush domain from price inputs
  const brushDomain: [number, number] | undefined = useMemo(() => {
    const leftPrice = priceLower;
    const rightPrice = priceUpper;
    if (!leftPrice || !rightPrice) return undefined;

    const leftValue = parseFloat(leftPrice.toSignificant(8));
    const rightValue = parseFloat(rightPrice.toSignificant(8));

    if (isNaN(leftValue) || isNaN(rightValue)) {
      console.error('Invalid price values:', { leftValue, rightValue });
      return undefined;
    }
    return [leftValue, rightValue];
  }, [priceLower, priceUpper]);

  // Add a ref to track the latest priceInputs
  const latestPriceInputsRef = useRef(priceInputs);

  // Update the ref whenever priceInputs changes
  useEffect(() => {
    latestPriceInputsRef.current = priceInputs;
  }, [priceInputs]);

  const onBrushDomainChange = useCallback(
    (domain: [number, number], mode: string | undefined) => {
      if (!currentPrice) return;

      let [leftRangeValue, rightRangeValue] = domain;

      if (leftRangeValue <= 0) {
        leftRangeValue = 1 / 10 ** 6;
      }

      // Handle reset mode specifically
      if (mode === 'reset') {
        // Use the ref instead of the captured priceInputs
        const currentPriceInputs = latestPriceInputsRef.current;
        handleChangePriceLower(currentPriceInputs.lower.initial);
        handleChangePriceUpper(currentPriceInputs.upper.initial);
        return;
      }

      if (mode === 'handle' || mode === 'drag') {
        if (!tickAtLimit[isSorted ? Bound.LOWER : Bound.UPPER] && leftRangeValue > 0) {
          handleChangePriceLower(leftRangeValue.toFixed(8));
        }
        if (!tickAtLimit[isSorted ? Bound.UPPER : Bound.LOWER] && rightRangeValue > 0) {
          if (rightRangeValue < 1e35) {
            handleChangePriceUpper(rightRangeValue.toFixed(8));
          }
        }
      }
    },
    [isSorted, handleChangePriceLower, handleChangePriceUpper, tickAtLimit, currentPrice],
  );

  // Disable interactivity if editing existing position or no chart data available
  const interactive = !positionId;

  // Determine if chart is uninitialized (no tokens selected)
  const isUninitialized = !currencyA || !currencyB;

  if (isUninitialized) {
    return (
      <LiquidityDepthChart
        poolKey={poolKey}
        poolState={poolState}
        tickAtLimit={tickAtLimit}
        invertPrices={invertPrices}
        currentPrice={0}
        interactive={false}
        uninitializedMessage="Your position will appear here."
        testID="liquidity-chart"
      />
    );
  }

  return (
    <LiquidityDepthChart
      poolKey={poolKey}
      poolState={poolState}
      tickAtLimit={tickAtLimit}
      invertPrices={invertPrices}
      brushDomain={brushDomain}
      currentPrice={currentPrice}
      interactive={interactive}
      onBrushDomainChange={onBrushDomainChange}
      testID="liquidity-chart"
    />
  );
});
